<template>
  <v-dialog :model-value="modelValue" max-width="680" @update:model-value="emit('update:modelValue', $event)">
    <v-card class="tag-restore-dialog">
      <v-card-title class="tag-restore-dialog__title">Restore a Tag</v-card-title>
      <v-card-text>
        <div class="tag-restore-list__header">
          <span class="tag-restore-list__dot-header"></span>
          <span>Tag</span>
          <span>Date</span>
          <span>Description</span>
        </div>

        <div v-if="loading" class="tag-restore-list__state">Loading tags...</div>
        <div v-else-if="!normalizedTags.length" class="tag-restore-list__state">No tags available.</div>

        <div v-else class="tag-restore-list">
          <div
            v-for="tag in normalizedTags"
            :key="tag.key"
            class="tag-restore-list__row"
            :class="{ 'tag-restore-list__row--selected': selectedTag?.key === tag.key }"
            @click="selectedTag = tag"
          >
            <span class="tag-restore-list__dot-cell">
              <span
                v-if="tag.isCurrent"
                v-tooltip:center.top="'Current Active Tag'"
                class="tag-restore-list__current-dot"
              ></span>
            </span>
            <span v-tooltip:center.top="tag.name" class="tag-restore-list__tag">{{ tag.name }}</span>
            <span class="tag-restore-list__date">{{ formatDate(tag.date) }}</span>
            <span class="tag-restore-list__description">{{ tag.description }}</span>
          </div>
        </div>

      </v-card-text>
      <v-card-actions class="tag-restore-dialog__actions">
        <div class="tag-restore-dialog__message">
          <span v-if="selectedTag">Restoring {{ selectedTag.name }}</span>
        </div>
        <div class="tag-restore-dialog__buttons">
          <v-btn class="button-light" variant="flat" @click="emit('update:modelValue', false)">Cancel</v-btn>
          <v-btn
            v-if="selectedTag"
            color="primary"
            variant="flat"
            :loading="restoring"
            @click="restoreSelectedTag"
          >
            Restore
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref } from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  datasetId: {
    type: [String, Number],
    default: ''
  },
  tags: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['update:modelValue', 'restored']);
const selectedTag = ref(null);
const restoring = ref(false);

const safeValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return String(value);
};

const formatDate = (value) => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return safeValue(value);
  }
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const normalizedTags = computed(() => {
  return (props.tags || []).map((item, index) => ({
    key: item?.id || item?.uid || item?.name || index,
    name: safeValue(item?.name || item?.tag || item?.tad),
    rawName: item?.name || item?.tag || item?.tad || '',
    isCurrent: Boolean(item?.is_current),
    date: item?.created_at || item?.date || item?.createdAt || null,
    description: safeValue(item?.description || item?.tag_description || item?.message)
  })).sort((a, b) => {
    const aDate = a.date ? new Date(a.date).getTime() : 0;
    const bDate = b.date ? new Date(b.date).getTime() : 0;
    return bDate - aDate;
  });
});

const restoreSelectedTag = async () => {
  if (!selectedTag.value || !props.datasetId) {
    return;
  }

  restoring.value = true;
  try {
    const dsConn = new DatastoreConnect();
    await dsConn.api('version.tag.restore', {
      dataset_id: props.datasetId,
      name: selectedTag.value.rawName || selectedTag.value.name,
    });
    emit('restored', selectedTag.value.rawName || selectedTag.value.name);
    emit('update:modelValue', false);
  } catch (error) {
    console.error('Error restoring tag:', error);
  } finally {
    restoring.value = false;
  }
};
</script>

<style lang="scss" scoped>
.tag-restore-dialog {
  @include themify() {
    background: themed('content-background');
    color: themed('body-text-color');
  }

  &__title {
    background: var(--color-primary);
    color: #fff;
    font-size: 1rem;
    font-weight: 700;
  }

  &__actions {
    border-top: 1px solid rgba(var(--color-primary-rgb), 0.15);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px 12px;
    gap: 10px;
  }

  &__message {
    font-size: 0.85rem;
    font-weight: 600;
    color: var(--body-text-color);
    min-height: 20px;
  }

  &__buttons {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
  }
}

.tag-restore-list__header {
  display: grid;
  grid-template-columns: 16px minmax(130px, 1fr) minmax(170px, 1fr) minmax(220px, 2fr);
  gap: 6px;
  align-items: center;
}

.tag-restore-list__header {
  padding: 8px 0;
  color: var(--color-primary);
  font-size: 0.78rem;
  font-weight: 700;

  > span:not(.tag-restore-list__dot-header) {
    border-bottom: 1px solid rgba(var(--color-primary-rgb), 0.2);
    padding-bottom: 8px;
  }
}

.tag-restore-list {
  margin-top: 6px;
  display: flex;
  flex-direction: column;
  gap: 2px;

  &__row {
    display: grid;
    grid-template-columns: 16px minmax(130px, 1fr) minmax(170px, 1fr) minmax(220px, 2fr);
    gap: 6px;
    align-items: center;
    padding: 0;
    border-radius: 6px;
    transition: background 0.15s ease;

    > span {
      padding: 10px 0;
      font-size: 0.8rem;
      color: var(--body-text-color-secondary);
      transition: color 0.15s ease;
    }

    > span:not(.tag-restore-list__dot-cell) {
      border-bottom: 1px solid rgba(var(--color-primary-rgb), 0.08);
    }

    &:hover {
      background: rgba(var(--color-primary-rgb), 0.1);
    }

    &--selected {
      background: rgba(var(--color-primary-rgb), 0.15);
    }

    &:hover > span {
      color: var(--body-text-color);
    }
  }

  &__tag {
    font-weight: 600;
    color: var(--body-text-color);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  &__dot-header {
    width: 16px;
  }

  &__dot-cell {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
  }

  &__current-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #2e7d32;
    flex: 0 0 auto;
  }

  &__description {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__state {
    padding: 16px 10px;
    color: var(--body-text-color-secondary);
    font-size: 0.85rem;
  }
}

</style>